"use client";

import { Capacitor } from "@capacitor/core";

import {
  registerDevicePushToken,
  unregisterDevicePushToken,
  type PushPlatform,
} from "@/lib/push/api";
import { isNativeApp, nativeUrlToAppPath } from "@/lib/native/platform";
import { getSupabaseBrowserClient } from "@/lib/supabase/client";

type RouterLike = {
  push: (href: string) => void;
};

function platformFromCapacitor(): PushPlatform | null {
  const platform = Capacitor.getPlatform();
  if (platform === "ios" || platform === "android") {
    return platform;
  }
  return null;
}

export async function startNativePush(input: {
  router: RouterLike;
  onCleanup: (cleanup: () => void) => void;
}) {
  if (!isNativeApp() || !Capacitor.isNativePlatform()) {
    return;
  }

  const platform = platformFromCapacitor();
  if (!platform) {
    return;
  }

  const supabase = getSupabaseBrowserClient();
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) {
    return;
  }

  const { PushNotifications } = await import("@capacitor/push-notifications");

  let currentToken: string | null = null;

  const registration = await PushNotifications.addListener(
    "registration",
    (token) => {
      currentToken = token.value;
      void registerDevicePushToken({
        platform,
        token: token.value,
      }).catch((error) => {
        console.error("Push token regisztráció sikertelen.", error);
      });
    },
  );
  input.onCleanup(() => void registration.remove());

  const registrationError = await PushNotifications.addListener(
    "registrationError",
    (error) => {
      console.error("Push regisztráció hiba.", error);
    },
  );
  input.onCleanup(() => void registrationError.remove());

  const received = await PushNotifications.addListener(
    "pushNotificationReceived",
    () => {
      // Foreground: in-app Realtime list handles UX.
    },
  );
  input.onCleanup(() => void received.remove());

  const action = await PushNotifications.addListener(
    "pushNotificationActionPerformed",
    (event) => {
      const data = event.notification.data as Record<string, unknown>;
      const target =
        typeof data.target_path === "string"
          ? data.target_path
          : typeof data.targetPath === "string"
            ? data.targetPath
            : null;
      if (!target) {
        input.router.push("/notifications");
        return;
      }
      if (target.startsWith("/")) {
        input.router.push(target);
        return;
      }
      const mapped =
        nativeUrlToAppPath(
          `https://praxistars.hu/${target.replace(/^\/+/, "")}`,
        ) || `/${target.replace(/^\/+/, "")}`;
      input.router.push(mapped);
    },
  );
  input.onCleanup(() => void action.remove());

  const permission = await PushNotifications.checkPermissions();
  let receive = permission.receive;
  if (receive === "prompt" || receive === "prompt-with-rationale") {
    const requested = await PushNotifications.requestPermissions();
    receive = requested.receive;
  }

  if (receive !== "granted") {
    return;
  }

  await PushNotifications.register();

  input.onCleanup(() => {
    if (!currentToken) {
      return;
    }
    void unregisterDevicePushToken({
      platform,
      token: currentToken,
    }).catch(() => undefined);
  });
}
